import { useMemo } from "react";
import { useGetCurrencies } from "./useExchangeRates";

export const useCurrencyConverter = () => {
	const { data: currencies = [], isLoading } = useGetCurrencies();

	const rateMap = useMemo(() => {
		const map = {};
		for (const c of currencies) {
			const code = c.currency_code?.toUpperCase();
			if (!code) continue;

			const zohoRate = parseFloat(c.exchange_rate) || 0;

			map[code] = {
				currency_id: c.currency_id,
				currency_code: code,
				currency_name: c.currency_name,
				symbol: c.currency_symbol === "NGN" ? "₦" : c.currency_symbol,
				zoho_rate: c.is_base_currency ? 1 : zohoRate, 
				user_rate: c.is_base_currency
					? 1
					: c.user_rate || (zohoRate > 0 ? 1 / zohoRate : 0),  
				effective_date: c.effective_date,
				is_base: c.is_base_currency,
				hasRate: c.is_base_currency || zohoRate > 0,
			};
		}
		return map;
	}, [currencies]);

	const baseCurrency = useMemo(
		() =>
			currencies
				.find((c) => c.is_base_currency)
				?.currency_code?.toUpperCase() || "USD",
		[currencies]
	);

	const toBase = (amount, fromCurrency) => {
		const code = fromCurrency?.toUpperCase();
		if (code === baseCurrency) return parseFloat(amount);
		const entry = rateMap[code];
		if (!entry || !entry.hasRate) return null;
		return parseFloat(amount) * entry.zoho_rate;
	};

	const fromBase = (amount, toCurrency) => {
		const code = toCurrency?.toUpperCase();
		if (code === baseCurrency) return parseFloat(amount);
		const entry = rateMap[code];
		if (!entry || !entry.hasRate) return null;
		return parseFloat(amount) / entry.zoho_rate;
	};

	const convert = (amount, fromCurrency, toCurrency) => {
		const from = fromCurrency?.toUpperCase();
		const to = toCurrency?.toUpperCase();
		if (from === to) return parseFloat(amount);

		const inBase =
			from === baseCurrency ? parseFloat(amount) : toBase(amount, from);
		if (inBase === null) return null;

		const result = to === baseCurrency ? inBase : fromBase(inBase, to);
		return result;
	};

	const format = (amount, currencyCode, decimals = 2) => {
		const entry = rateMap[currencyCode?.toUpperCase()];
		const sym = entry?.symbol || currencyCode || "";
		const n = parseFloat(amount);
		if (amount === null || amount === undefined || isNaN(n)) return `${sym} —`;
		return `${sym} ${n.toLocaleString(undefined, {
			minimumFractionDigits: decimals,
			maximumFractionDigits: decimals,
		})}`;
	};

	const getZohoRate = (currencyCode) => {
		const entry = rateMap[currencyCode?.toUpperCase()];
		if (!entry || !entry.hasRate) return null;
		return entry.zoho_rate; 
	};

	const getUserRate = (currencyCode) => {
		const entry = rateMap[currencyCode?.toUpperCase()];
		if (!entry || !entry.hasRate) return null;
		return entry.user_rate;
	};

	const hasRate = (currencyCode) => {
		return !!rateMap[currencyCode?.toUpperCase()]?.hasRate;
	};

	const currencyList = useMemo(
		() =>
			Object.values(rateMap).sort((a, b) => {
				if (a.is_base) return -1;
				if (b.is_base) return 1;
				return a.currency_code.localeCompare(b.currency_code);
			}),
		[rateMap]
	);

	const convertibleList = useMemo(
		() => currencyList.filter((c) => c.hasRate),
		[currencyList]
	);

	return {
		convert,
		toBase,
		fromBase,
		format,
		getZohoRate,
		getUserRate,
		hasRate,
		rateMap,
		baseCurrency,
		currencyList,
		convertibleList,
		loading: isLoading,
	};
};
